/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaAssigneeRoster.ts
 * FILE_ID: JOBHUB-QA-ASSIGNEE-ROSTER-001
 * PURPOSE: One open-id plus assignable org people for QA assignee chips.
 */
import { useCallback, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useJobAccessMeta } from '../../access'
import { useJobHubDataAdapter } from '../../jobHubDataAdapter'
import type { QaAssigneeCandidate } from '../../qa/qaTypes'

type QaAssignPayload = {
  can_assign?: boolean
  qa_assign_policy?: string
  candidates?: QaAssigneeCandidate[]
}

export function useQaAssigneeRoster(jobId: number) {
  const { client } = useJobHubDataAdapter()
  const access = useJobAccessMeta(jobId)
  const [openAssigneeId, setOpenAssigneeId] = useState<string | null>(null)
  const assignQuery = useQuery<QaAssignPayload>({
    queryKey: ['job-qa-assign', jobId],
    queryFn: () => client.get(`/jobs/${jobId}/qa-assign/`).then((response) => response.data),
    enabled: Number.isFinite(jobId) && jobId > 0,
  })
  const teamQuery = useQuery<{ team?: Array<QaAssigneeCandidate & { is_active?: boolean }> }>({
    queryKey: ['job-team', jobId],
    queryFn: () => client.get(`/jobs/${jobId}/team`).then((response) => response.data),
    enabled: Number.isFinite(jobId) && jobId > 0,
  })
  const peopleQuery = useQuery<Array<{ email?: string | null; user_id?: number | null; name?: string | null }>>({
    queryKey: ['sam-people-directory'],
    queryFn: () => client.get('/people').then((response) => (
      Array.isArray(response.data) ? response.data : []
    )),
    enabled: Number.isFinite(jobId) && jobId > 0,
    staleTime: 5 * 60 * 1000,
    retry: 0,
  })
  const candidates = useMemo(() => {
    const byId = new Map<number, QaAssigneeCandidate>()
    const add = (row: QaAssigneeCandidate | undefined) => {
      if (!row?.user_id) return
      if (!byId.has(row.user_id)) byId.set(row.user_id, row)
    }
    for (const row of assignQuery.data?.candidates ?? []) add(row)
    for (const row of teamQuery.data?.team ?? []) {
      if (row.is_active === false) continue
      add(row)
    }
    const knownEmails = new Set(
      [...byId.values()].map((row) => (row.email || '').trim().toLowerCase()).filter(Boolean),
    )
    for (const person of peopleQuery.data ?? []) {
      const userId = person.user_id
      const email = (person.email || '').trim()
      if (!userId || !email) continue
      if (knownEmails.has(email.toLowerCase()) || byId.has(userId)) continue
      add({
        user_id: userId,
        name: person.name || email.split('@')[0],
        email,
      })
    }
    return [...byId.values()]
  }, [assignQuery.data, teamQuery.data, peopleQuery.data])
  const canAssign = Boolean(
    assignQuery.data?.can_assign
    ?? access.data?.canAssignQaTasks
    ?? access.data?.canManageJobTeam,
  )
  const searchCandidates = useCallback(async (q: string) => {
    const response = await client.get(`/jobs/${jobId}/qa-assign/`, { params: { q } })
    const rows = (response.data as QaAssignPayload | undefined)?.candidates
    return Array.isArray(rows) ? rows : []
  }, [client, jobId])
  return {
    canAssign,
    candidates,
    searchCandidates,
    policy: assignQuery.data?.qa_assign_policy ?? access.data?.qaAssignPolicy ?? 'project_manager',
    openAssigneeId,
    setOpenAssigneeId,
  }
}

export type QaAssigneeRoster = ReturnType<typeof useQaAssigneeRoster>
